import { useMemo, useState } from "react";

/** Allowed sort modes for {@link useResourcesFiltersAndSort}. */
export type SortOption = "Newest" | "Oldest" | "A–Z" | "Z–A";

export interface FiltersAndSortConfig<T> {
  /** The full list to filter and sort. */
  items: T[];
  /** Accessor returning the item's title — used for search and for A–Z / Z–A sorting. */
  getTitle: (item: T) => string;
  /** Accessor returning a secondary string searched alongside the title (e.g. description / summary). */
  getSearchText: (item: T) => string;
  /** Accessor returning the item's date as an ISO-ish string parseable by `new Date(...)`. */
  getDate: (item: T) => string;
  /**
   * Decides whether an item belongs to the currently active category.
   * The active value `"All"` is passed through and the caller is expected to
   * return `true` for it.
   */
  matchesCategory: (item: T, activeCategory: string) => boolean;
  /** Sort mode to start with. Defaults to `"Newest"`. */
  initialSort?: SortOption;
}

/**
 * Shared search / category-filter / sort logic for resource-style lists
 * (used by `ResourcesOverview` and `GuideOverview`).
 *
 * The hook owns three pieces of state — `query`, `activeCategory`,
 * `sortOption` — and returns the post-filter, post-sort list as `result`.
 * Callers supply the accessors so the same hook can drive lists with
 * different shapes.
 *
 * Search is case-insensitive and matches if either `getTitle(item)` or
 * `getSearchText(item)` contains the trimmed query.
 */
export default function useResourcesFiltersAndSort<T>({
  items,
  getTitle,
  getSearchText,
  getDate,
  matchesCategory,
  initialSort = "Newest",
}: FiltersAndSortConfig<T>) {
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [sortOption, setSortOption] = useState<SortOption>(initialSort);

  const result = useMemo(() => {
    const q = query.toLowerCase().trim();

    const filtered = items.filter((item) => {
      const matchQuery =
        getTitle(item).toLowerCase().includes(q) ||
        getSearchText(item).toLowerCase().includes(q);
      return matchQuery && matchesCategory(item, activeCategory);
    });

    switch (sortOption) {
      case "A–Z":
        return filtered.sort((a, b) => getTitle(a).localeCompare(getTitle(b)));
      case "Z–A":
        return filtered.sort((a, b) => getTitle(b).localeCompare(getTitle(a)));
      case "Oldest":
        return filtered.sort(
          (a, b) => new Date(getDate(a)).getTime() - new Date(getDate(b)).getTime()
        );
      case "Newest":
      default:
        return filtered.sort(
          (a, b) => new Date(getDate(b)).getTime() - new Date(getDate(a)).getTime()
        );
    }
  }, [items, query, activeCategory, sortOption, getTitle, getSearchText, getDate, matchesCategory]);

  return {
    query,
    setQuery,
    activeCategory,
    setActiveCategory,
    sortOption,
    setSortOption,
    result,
  };
}
